import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BANKING_PERMISSIONS } from "@folio/shared/permissions";
import { usePermissions } from "@/lib/permissions";
import { apiFetch } from "@/lib/api-client";
import type { AccountsResponse, Account } from "@/features/accounts/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { bankingApi } from "../api";
import { BankSelector } from "./BankSelector";
import { useBankingRequest } from "../hooks/useBankingRequest";
import type {
  BankAccountDto,
  BankAccountFieldsDto,
  BankingCurrencyDto,
  BankingPageDto,
} from "../types";
import { BankingError, BankingPager, BankingPanel } from "./BankingShared";

const blank: BankAccountFieldsDto = {
  bankLabel: "",
  name: "",
  accountNumber: "",
  currency: "CLP",
  accountingAccountId: "",
  active: true,
};
export function BankAccounts() {
  const { hasPermission, isLoading } = usePermissions();
  const write = !isLoading && hasPermission(BANKING_PERMISSIONS.accountWrite);
  const [page, setPage] = useState<BankingPageDto<BankAccountDto> | null>(null);
  const [editing, setEditing] = useState<BankAccountDto | null | undefined>(
    undefined,
  );
  const [reload, setReload] = useState(0);
  const list = useBankingRequest();
  const { run } = list;
  useEffect(() => {
    void run((signal) => bankingApi.accounts(undefined, signal), setPage);
  }, [run, reload]);
  return (
    <div className="space-y-4">
      <BankingPanel title="Cuentas bancarias">
        <p>
          Configura la cuenta de la organización seleccionada. Los cambios no
          alteran los valores históricos de importaciones confirmadas.
        </p>
        <BankingError error={list.error} />
        {write && (
          <Button onClick={() => setEditing(null)}>
            Registrar cuenta bancaria
          </Button>
        )}
        {list.pending && <p role="status">Cargando cuentas…</p>}
        {page?.items.length === 0 && <p>Sin cuentas registradas.</p>}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                {[
                  "Banco",
                  "Nombre",
                  "Número",
                  "Moneda",
                  "Cuenta contable",
                  "Estado",
                  "Acciones",
                ].map((h) => (
                  <th className="p-2" key={h}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {page?.items.map((account) => (
                <tr className="border-t" key={account.id}>
                  <td className="p-2">{account.bankLabel}</td>
                  <td className="p-2">
                    {account.name}
                    <p className="select-all text-xs">ID: {account.id}</p>
                  </td>
                  <td className="p-2">{account.accountNumber}</td>
                  <td className="p-2">{account.currency}</td>
                  <td className="p-2">{account.accountingAccountId}</td>
                  <td className="p-2">
                    {account.active ? "Activa" : "Inactiva"} · rev.{" "}
                    {account.revision}
                  </td>
                  <td className="space-x-2 p-2">
                    {write && (
                      <Button
                        variant="outline"
                        onClick={() => setEditing(account)}
                      >
                        Editar {account.name}
                      </Button>
                    )}
                    {hasPermission(BANKING_PERMISSIONS.importRead) && (
                      <Button variant="link" asChild>
                        <Link
                          to={`/contabilidad/bancos?account=${encodeURIComponent(account.id)}`}
                        >
                          Consultar / Importar
                        </Link>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <BankingPager
          pending={list.pending}
          next={page?.nextCursor}
          onReset={() => {
            setPage(null);
            setReload((n) => n + 1);
          }}
          onNext={() =>
            void list.run(
              (signal) =>
                bankingApi.accounts(page?.nextCursor ?? undefined, signal),
              setPage,
            )
          }
        />
      </BankingPanel>
      {write && editing !== undefined && (
        <AccountForm
          key={editing?.id ?? "new"}
          account={editing}
          onCancel={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            setPage(null);
            setReload((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}

function AccountForm({
  account,
  onCancel,
  onSaved,
}: {
  account: BankAccountDto | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [fields, setFields] = useState<BankAccountFieldsDto>(() =>
    account
      ? {
          bankLabel: account.bankLabel,
          name: account.name,
          accountNumber: account.accountNumber,
          currency: account.currency,
          accountingAccountId: account.accountingAccountId,
          active: account.active,
        }
      : { ...blank },
  );
  const [chart, setChart] = useState<Account[]>([]);
  const save = useBankingRequest();
  const lookup = useBankingRequest();
  const { run } = lookup;
  const { hasPermission, isLoading } = usePermissions();
  const readChart = !isLoading && hasPermission("contabilidad:cuentas:read");
  const write = !isLoading && hasPermission(BANKING_PERMISSIONS.accountWrite);
  useEffect(() => {
    if (readChart)
      void run(
        (signal) => apiFetch<AccountsResponse>("/api/accounts", { signal }),
        (result) => setChart(result.accounts),
      );
  }, [readChart, run]);
  return (
    <BankingPanel
      title={account ? "Editar cuenta bancaria" : "Registrar cuenta bancaria"}
    >
      <form
        className="grid gap-4 md:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (!save.pending && (account || fields.bankLabel))
            void save.run(
              (signal) => bankingApi.saveAccount(fields, account, signal),
              onSaved,
              write,
            );
        }}
      >
        {account ? (
          <div className="space-y-2">
            <Label htmlFor="bank-bankLabel">Banco</Label>
            <Input
              id="bank-bankLabel"
              value={fields.bankLabel}
              required
              maxLength={4096}
              onChange={(event) =>
                setFields((f) => ({ ...f, bankLabel: event.target.value }))
              }
            />
          </div>
        ) : (
          <BankSelector
            value={fields.bankLabel}
            onChange={(bankLabel) => setFields((f) => ({ ...f, bankLabel }))}
          />
        )}
        {(
          [
            ["name", "Nombre de cuenta"],
            ["accountNumber", "Número de cuenta (conserva ceros iniciales)"],
          ] as const
        ).map(([key, label]) => (
          <div key={key} className="space-y-2">
            <Label htmlFor={`bank-${key}`}>{label}</Label>
            <Input
              id={`bank-${key}`}
              value={fields[key]}
              required
              maxLength={4096}
              onChange={(e) =>
                setFields((f) => ({ ...f, [key]: e.target.value }))
              }
            />
          </div>
        ))}
        <div className="space-y-2">
          <Label htmlFor="bank-currency">Moneda</Label>
          <select
            id="bank-currency"
            className="h-10 w-full rounded border bg-background px-3"
            value={fields.currency}
            onChange={(e) =>
              setFields((f) => ({
                ...f,
                currency: e.target.value as BankingCurrencyDto,
              }))
            }
          >
            {[
              "CLP",
              "USD",
              "EUR",
              "MXN",
              "ARS",
              "COP",
              "BRL",
              "PEN",
              "UYU",
            ].map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
          <p className="text-sm text-muted-foreground">
            Debe estar habilitada para esta organización; el servidor verifica
            la configuración.
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="bank-accounting">
            ID de cuenta contable asociada
          </Label>
          <Input
            id="bank-accounting"
            list="bank-chart"
            value={fields.accountingAccountId}
            required
            onChange={(e) =>
              setFields((f) => ({ ...f, accountingAccountId: e.target.value }))
            }
          />
          <datalist id="bank-chart">
            {chart
              .filter((a) => a.isActive && !a.isHeader)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code} — {a.name}
                </option>
              ))}
          </datalist>
          <p className="text-sm text-muted-foreground">
            Ingresa una cuenta de contabilización activa de la organización
            seleccionada. Las sugerencias provienen del plan de cuentas de la
            empresa; el servidor verifica la asociación.
          </p>
          <BankingError error={lookup.error} />
        </div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={fields.active}
            onChange={(e) =>
              setFields((f) => ({ ...f, active: e.target.checked }))
            }
          />
          Activa
        </label>
        <div className="md:col-span-2">
          <BankingError error={save.error} />
          {account && (
            <p className="text-sm">
              Revisión {account.revision}. Ante un conflicto, cancela y
              actualiza la lista antes de editar nuevamente.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            type="submit"
            disabled={!write || save.pending || (!account && !fields.bankLabel)}
          >
            Guardar cuenta
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={save.pending}
            onClick={onCancel}
          >
            Cancelar
          </Button>
        </div>
      </form>
    </BankingPanel>
  );
}
